import { useEffect, useRef } from "react";
import { sanitizeHtml } from "@/lib/sanitize";
import { Bold, Italic, Link2, Eraser } from "lucide-react";

/**
 * Small rich-text field for the page editor: bold, italic and links.
 * Stores sanitized HTML.
 */
export default function RichTextField({ value, onChange, testid }) {
  const ref = useRef(null);
  const lastPushed = useRef(null);
  const mounted = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    if (!mounted.current || value !== lastPushed.current) {
      ref.current.innerHTML = value || "";
      lastPushed.current = value;
      mounted.current = true;
    }
  }, [value]);

  const push = () => {
    const html = sanitizeHtml(ref.current.innerHTML);
    lastPushed.current = html;
    onChange(html);
  };

  const cmd = (command, arg) => {
    ref.current.focus();
    document.execCommand(command, false, arg);
    push();
  };

  const addLink = () => {
    const url = window.prompt("Adresse du lien (https://… ou /contact)");
    if (!url) return;
    cmd("createLink", url);
  };

  return (
    <div className="mt-1 border border-[#D9D3C5] bg-white focus-within:border-[#B8956A]">
      <div className="flex items-center gap-1 border-b border-[#EFE7DA] px-2 py-1.5">
        {[
          { icon: Bold, action: () => cmd("bold"), label: "Gras", id: "bold" },
          { icon: Italic, action: () => cmd("italic"), label: "Italique", id: "italic" },
          { icon: Link2, action: addLink, label: "Lien", id: "link" },
          { icon: Eraser, action: () => cmd("removeFormat"), label: "Effacer la mise en forme", id: "clear" },
        ].map((b) => (
          <button
            key={b.id}
            type="button"
            title={b.label}
            aria-label={b.label}
            data-testid={`${testid}-${b.id}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={b.action}
            className="p-1.5 text-[#697585] hover:text-[#B8956A] hover:bg-[#F6F0E7]"
          >
            <b.icon size={14} />
          </button>
        ))}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-testid={testid}
        onInput={push}
        onBlur={push}
        className="px-3 py-2 text-sm text-[#203240] min-h-[90px] outline-none leading-relaxed [&_a]:text-[#B8956A] [&_a]:underline"
      />
    </div>
  );
}
